import { inject, Injectable } from '@angular/core';
import { MockService } from '@mock/mock.service';
import { delay, Observable } from 'rxjs';
import dayjs from 'dayjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { IPaginationMeta, IPaginationRequest } from '@app/api/interfaces/common/pagination-meta.interface';
import { IDevicePicture, IDevicePictureUpdate } from '@app/api/interfaces/device-picture.interface';
import { PictureRotation } from '@app/core/enums/picture-rotation.enum';
import { LoggerDeploymentsDataMockService } from '@mock/data-services/logger-deployments-data-mock.service';
import { convertStringInToSumOfAllCharacters } from '@app/shared/utils/string';
import { enumToArray } from '@app/shared/utils/enumerator';
import { getRandomItem } from '@app/shared/utils/random';

const MOCK_IMAGE_URLS = [
  'imgs/mock/mock-img-1.jpg',
  'imgs/mock/mock-img-2.jpg',
  'imgs/mock/mock-img-3.jpg',
  'imgs/mock/mock-img-4.jpg',
];

@Injectable({ providedIn: 'root' })
export class LoggerDeploymentPicturesMockService extends MockService {
  private LoggerDeploymentsDataMockService = inject(LoggerDeploymentsDataMockService);

  public devicePictures: IDevicePicture[] = [];

  private id = 1;

  constructor() {
    super();

    this.generateDevicePictures();
  }

  public getAll(
    projectId: string,
    deploymentId: string,
    pagination: Partial<IPaginationRequest> = {}
  ): Observable<IApiResponse<IDevicePicture[], IPaginationMeta>> {
    this.generateDevicePictures();

    const devicePictures = this.devicePictures.filter(devicePicture => devicePicture.deploymentId === deploymentId);

    const offset = pagination.offset ?? 0;
    const limit = pagination.limit ?? devicePictures.length;

    const pagedDevicePictures = devicePictures.slice(offset, offset + limit);

    return this.mockResponse(
      'LOGGER_DEPLOYMENT_PICTURES:getAll',
      {
        data: pagedDevicePictures,
        meta: {
          pagination: { count: pagedDevicePictures.length, total: devicePictures.length, offset, limit },
        },
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  public getOne(
    projectId: string,
    deploymentId: string,
    pictureId: string
  ): Observable<IApiResponse<IDevicePicture, object>> {
    const devicePicture = this.devicePictures.find(item => item.id === pictureId);

    return this.mockResponse(
      'LOGGER_DEPLOYMENT_PICTURES:getOne',
      {
        data: devicePicture as IDevicePicture,
        meta: {},
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  public update(
    projectId: string,
    deploymentId: string,
    pictureId: string,
    body: IDevicePictureUpdate
  ): Observable<IApiResponse<IDevicePicture, object>> {
    const devicePicture = this.devicePictures.find(item => item.id === pictureId);

    if (devicePicture) {
      const index = this.devicePictures.indexOf(devicePicture);
      this.devicePictures.splice(index, 1, {
        ...devicePicture,
        rotation: body.rotation,
        modifiedAt: dayjs().toISOString(),
      });
    }

    return this.mockResponse(
      'LOGGER_DEPLOYMENT_PICTURES:update',
      {
        data: this.devicePictures.find(item => item.id === pictureId) as IDevicePicture,
        meta: {},
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  private generateDevicePictures(): void {
    if (this.devicePictures.length > 0) {
      return;
    }

    const devicePictures: IDevicePicture[] = [];

    this.LoggerDeploymentsDataMockService.loggerDeploymentsData.forEach(loggerDeploymentData => {
      const sum = convertStringInToSumOfAllCharacters(loggerDeploymentData.deploymentId);

      // some deployments have no pictures at all - decided by the sum of their id characters being even
      if (sum % 2 !== 0) return;

      const picturesCount = (sum % 4) + 1;

      for (let i = 0; i < picturesCount; i++) {
        devicePictures.push({
          id: `picture_${this.id++}`,
          deploymentId: loggerDeploymentData.deploymentId,
          imageUrl: MOCK_IMAGE_URLS[i % MOCK_IMAGE_URLS.length],
          rotation: getRandomItem(enumToArray(PictureRotation)),
          modifiedAt: dayjs().toISOString(),
        });
      }
    });

    this.devicePictures = devicePictures;
  }
}
